import { Box, Typography, Divider, CircularProgress } from "@mui/joy";
import { Plus } from "lucide-react";
import { useState } from "react";

import { DataGridSetting } from "../../../../../../components";
import { Button, Alert } from "../../../../../../shared";
import useLabor from "../../../../../../hooks/query/useLabor";

import { columnDef } from "./column-def";
import { LaborRateForm } from "./LaborRateForm";
import { Info } from "./GridFrameworks";

interface SupplierLaborRatesProps {
    supplierId: string;
}

export function SupplierLaborRates({ supplierId }: SupplierLaborRatesProps) {
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingRate, setEditingRate] = useState<any | null>(null);
    const { useListLaborInventory } = useLabor();
    const { data, isLoading, isError, error } = useListLaborInventory({ supplierId });

    const handleCreate = () => {
        setEditingRate(null);
        setIsFormOpen(true);
    };

    const handleEdit = (rate: any) => {
        setEditingRate(rate);
        setIsFormOpen(true);
    };

    const handleClose = () => {
        setIsFormOpen(false);
        setEditingRate(null);
    };

    if (isLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center" p={2}>
                <CircularProgress size="sm" />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box flex={1} p={2}>
                <Alert errorMessage={error instanceof Error ? error.message : "Failed to load labor rates"} />
            </Box>
        );
    }

    const rates = (data as any[]) || [];

    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", height: "100%" }}>
            <Box ml={3} mt={2} mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography level="title-md" sx={{ fontWeight: 600 }}>
                        Labor Rates
                    </Typography>
                    <Typography level="body-sm" textColor="text.tertiary">
                        Manage labor service prices for this supplier.
                    </Typography>
                </Box>
            </Box>
            <Divider />
            <Box flex={1} sx={{ overflow: "hidden" }}>
                <DataGridSetting
                    gridData={rates}
                    columnDefs={columnDef.map((col: any) => {
                        if (col.cellRenderer === Info || col.field === "Id") {
                            return { ...col, cellRenderer: Info, cellRendererParams: { onEdit: handleEdit } };
                        }
                        return col;
                    })}
                    quickControlStartDecorator={<CreateLaborRateButton onClick={handleCreate} />}
                />
            </Box>

            {isFormOpen && (
                <LaborRateForm supplierId={supplierId} rateItem={editingRate} onClose={handleClose} />
            )}
        </Box>
    );
}

function CreateLaborRateButton({ onClick }: { onClick: () => void }) {
    return (
        <Box display="flex" alignItems={"center"} ml={1.5}>
            <Button startDecorator={<Plus size={18} />} variant="soft" color="primary" onClick={onClick}>
                Add Labor Rate
            </Button>
            <Divider orientation="vertical" sx={{ mt: 1, mb: 1, ml: 2 }} />
        </Box>
    );
}
